import { useMutation, useQuery } from '@tanstack/react-query'
import cx from 'classnames'
import { useEffect, useState } from 'react'
import useBackendClient from '~/api/BackendClient/useBackendClient'
import type {
  CustomCultureStatusCategory,
  CustomCultureStatusIcon,
  CustomCultureStatusResponse,
} from '~/api/backend/customCultureStatusAPI'
import { queryClient } from '~/api/query-client'
import { QUERY_KEYS } from '~/api/query-client-keys'
import Button from '~/components/buttons/Button'
import Dialog from '~/components/Dialog'
import CultureStatusIcon from '~/components/icons/CultureStatusIcon'
import { CULTURE_STATUS_ICONS } from '~/utils/cultureStatus'
import cs from './settings_page.scss'

export default function SettingsPage() {
  const client = useBackendClient()
  const { data } = useQuery({
    queryFn: async () => client.customCultureStatuses.getAll(),
    queryKey: [QUERY_KEYS.CUSTOM_CULTURE_STATUS_LIST],
  })
  const activeStatuses =
    data
      ?.filter(s => s.category === 'active')
      .sort((a, b) => {
        return new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
      }) ?? []

  const terminatedStatuses =
    data
      ?.filter(s => s.category === 'terminated')
      .sort((a, b) => {
        return new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
      }) ?? []

  return (
    <div className={cs.page}>
      <h1 className={cs.title}>Settings</h1>

      <MCPTokenSection />

      <section className={cs.statuses}>
        <h2 className={cs.sectionTitle}>Culture Statuses</h2>
        <StatusCategorySection title='Active' statuses={activeStatuses} />
        <StatusCategorySection title='Terminated' statuses={terminatedStatuses} />
      </section>
    </div>
  )
}

function MCPTokenSection() {
  const client = useBackendClient()
  const [token, setToken] = useState<string | null>(null)
  const [tokenExpiry, setTokenExpiry] = useState<Date | null>(null)
  const [tokenError, setTokenError] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)
  const [copyError, setCopyError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  const decodeJWT = (token: string): { exp?: number } | null => {
    try {
      // Validate JWT structure (should have 3 parts separated by dots)
      const parts = token.split('.')
      if (parts.length !== 3) {
        console.error('Invalid JWT structure: expected 3 parts')
        return null
      }

      const base64Url = parts[1]
      if (!base64Url) {
        console.error('Invalid JWT: missing payload')
        return null
      }

      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
      // Pad base64 string to multiple of 4 characters
      const paddedBase64 = base64 + '='.repeat((4 - (base64.length % 4)) % 4)
      const jsonPayload = decodeURIComponent(
        atob(paddedBase64)
          .split('')
          .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join(''),
      )
      return JSON.parse(jsonPayload)
    } catch (error) {
      console.error('Failed to decode JWT:', error)
      return null
    }
  }

  const fetchToken = async () => {
    setLoading(true)
    setTokenError(null)
    try {
      const accessToken = await client.getAccessTokenSilently()
      setToken(accessToken)

      // Decode token to get expiry
      const decoded = decodeJWT(accessToken)
      if (decoded?.exp) {
        setTokenExpiry(new Date(decoded.exp * 1000))
      }
    } catch (error) {
      console.error('Failed to get access token:', error)
      setToken(null)
      setTokenError(
        'Could not retrieve token. Please try logging out and back in, or contact support if the issue persists.',
      )
    } finally {
      setLoading(false)
    }
  }

  const copyToken = async () => {
    if (token) {
      setCopyError(null)
      try {
        await navigator.clipboard.writeText(token)
        setCopied(true)
        setTimeout(() => setCopied(false), 2000)
      } catch (error) {
        console.error('Failed to copy token:', error)
        setCopyError(
          'Failed to copy to clipboard. Please copy the token manually or check browser permissions.',
        )
      }
    }
  }

  // Auto-clear expired tokens
  useEffect(() => {
    if (tokenExpiry && token) {
      const now = Date.now()
      const expiryTime = tokenExpiry.getTime()

      if (expiryTime <= now) {
        // Token is already expired
        return
      }

      // Set a timer to clear the token when it expires
      const timeUntilExpiry = expiryTime - now
      const timer = setTimeout(() => {
        setToken(null)
        setTokenExpiry(null)
        setTokenError('Your token has expired. Please fetch a new one.')
      }, timeUntilExpiry)

      return () => clearTimeout(timer)
    }
  }, [tokenExpiry, token])

  return (
    <section className={cs.mcpSection}>
      <h2 className={cs.sectionTitle}>MCP Integration</h2>
      <p className={cs.description}>
        Use this token to authenticate Cursor or other MCP clients with Monomer's
        backend.
      </p>

      <div className={cs.tokenActions}>
        <Button
          label={loading ? 'Loading...' : 'Show Token'}
          type='normal'
          variant='default'
          onClick={fetchToken}
          disabled={loading || token !== null}
          className={cs.button}
        />

        {token && (
          <>
            <Button
              label={copied ? 'Copied!' : 'Copy Token'}
              type='primary'
              variant='default'
              onClick={copyToken}
              className={cs.button}
            />
            <Button
              label='Hide'
              type='normal'
              variant='minimal'
              onClick={() => {
                setToken(null)
                setTokenExpiry(null)
                setTokenError(null)
                setCopyError(null)
                setCopied(false)
              }}
              className={cs.button}
            />
          </>
        )}
      </div>

      {tokenError && (
        <div className={cs.errorMessage}>
          <p>{tokenError}</p>
        </div>
      )}

      {copyError && (
        <div className={cs.errorMessage}>
          <p>{copyError}</p>
        </div>
      )}

      {token && (
        <div className={cs.tokenDisplay}>
          <code>{token}</code>
        </div>
      )}

      {token && (
        <div className={cs.instructions}>
          <p>
            <strong>To use with Cursor:</strong>
          </p>
          <ol>
            <li>Copy the token above</li>
            <li>
              Open <code>~/.cursor/mcp.json</code>
            </li>
            <li>Add the configuration:</li>
          </ol>
          <pre className={cs.codeBlock}>
            {`{
  "mcpServers": {
    "monomer-backend": {
      "url": "${process.env.MONOMER_BACKEND_V2_ORIGIN}/mcp",
      "headers": {
        "Authorization": "Bearer YOUR_TOKEN_HERE"
      }
    }
  }
}`}
          </pre>
          {tokenExpiry && (
            <>
              <p>
                <small>
                  Token expires: {tokenExpiry.toLocaleString()}
                  {(() => {
                    const remainingMs = tokenExpiry.getTime() - Date.now()
                    if (remainingMs <= 0) {
                      return ' (expired)'
                    }
                    const hours = Math.round(remainingMs / (1000 * 60 * 60))
                    return ` (${hours} hours remaining)`
                  })()}
                </small>
              </p>
              {tokenExpiry.getTime() <= Date.now() && (
                <div className={cs.errorMessage}>
                  <p>
                    <strong>This token has expired.</strong> Click "Hide" and then "Show
                    Token" to fetch a fresh token.
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </section>
  )
}

function StatusCategorySection({
  title,
  statuses,
}: {
  title: 'Active' | 'Terminated'
  statuses: CustomCultureStatusResponse[]
}) {
  const [createFormOpen, setCreateFormOpen] = useState(false)

  return (
    <div className={cs.statusCategorySection}>
      <div className={cs.header}>
        <h3>{title}</h3>{' '}
        <Button
          label='+ New'
          type='primary'
          variant='minimal'
          onClick={() => {
            setCreateFormOpen(true)
          }}
          className={cs.button}
        />
      </div>

      {statuses.map(status => (
        <StatusLineItem key={status.name} status={status} />
      ))}

      {createFormOpen ? (
        <CreateNewStatusForm
          category={title.toLowerCase() as CustomCultureStatusCategory}
          onCancel={() => {
            setCreateFormOpen(false)
          }}
        />
      ) : null}
    </div>
  )
}

function StatusLineItem({ status }: { status: CustomCultureStatusResponse }) {
  const [editing, setEditing] = useState(false)
  const [editingName, setEditingName] = useState(status.name)
  const [editingIcon, setEditingIcon] = useState(status.icon)
  const client = useBackendClient()
  const [isOpen, setisOpen] = useState(false)
  const { mutate } = useMutation({
    mutationFn: async (body: {
      name: string | null
      icon: CustomCultureStatusIcon | null
      category: CustomCultureStatusCategory | null
      is_archived: boolean
    }) =>
      await client.customCultureStatuses.update(status.id, {
        is_archived: body.is_archived,
        icon: body.icon,
        name: body.name,
        category: body.category,
      }),
    onSuccess: async () => {
      // We invalidate the following because
      // 1. Culture status list requires a refetch
      // 2. The name of a status depends on the row joined from statuschangeevent table
      // 3. Culture plate status depends on the row joined from statuschangeevent table
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEYS.CUSTOM_CULTURE_STATUS_LIST],
        }),
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEYS.WELL_PAGE],
        }),
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEYS.PLATE_PAGE],
        }),
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEYS.CULTURE_PAGE_FILTER_DEFINITIONS],
        }),
      ])
      setEditing(false)
    },
  })

  return (
    <div
      key={status.name}
      className={cx(cs.status, { [cs.editing]: status.type === 'custom' && editing })}
    >
      <div>
        <Dialog
          className={cs.dialog}
          onClose={() => {
            setisOpen(false)
          }}
          isOpen={isOpen}
        >
          <p className={cs.dialogTitle}>
            Are you sure you want to archive this status? <br />
            This cannot be undone.
          </p>
          <div className={cs.buttons}>
            <Button
              label='No'
              type='normal'
              variant='default'
              onClick={() => {
                setisOpen(false)
              }}
            />
            <Button
              label='Yes'
              type='danger'
              variant='default'
              onClick={() => {
                mutate({
                  name: null,
                  icon: null,
                  category: null,
                  is_archived: true,
                })
              }}
            />
          </div>
        </Dialog>
        {editing ? (
          <div>
            <CultureStatusIconPicker
              value={editingIcon}
              onChange={newIcon => {
                setEditingIcon(newIcon)
              }}
            />
            <input
              value={editingName}
              onChange={e => {
                if (e.target.value.trim().length <= 20) {
                  setEditingName(e.target.value)
                }
              }}
            />
            {editingName.length >= 18 && (
              <div className={cs.warning}>A status can only be 20 characters long</div>
            )}
          </div>
        ) : (
          <>
            <CultureStatusIcon kind={status.icon} className={cs.mainIcon} />{' '}
            {status.name}
          </>
        )}
      </div>

      <div className={cs.controls}>
        {editing ? (
          <>
            <Button
              label='Cancel'
              type='danger'
              variant='minimal'
              onClick={() => {
                setEditing(false)
                setEditingName(status.name)
                setEditingIcon(status.icon)
              }}
              className={cs.button}
            />
            <Button
              label='Save'
              type='primary'
              variant='minimal'
              onClick={() => {
                mutate({
                  name: editingName,
                  icon: editingIcon,
                  category: status.category,
                  is_archived: false,
                })
              }}
              className={cs.button}
            />
          </>
        ) : status.type === 'system' ? null : (
          <>
            <Button
              label='Edit'
              type='normal'
              variant='minimal'
              onClick={() => {
                setEditing(true)
              }}
              className={cs.button}
            />
            <Button
              label='Archive'
              type='normal'
              variant='minimal'
              className={cs.button}
              onClick={() => setisOpen(true)}
            />
          </>
        )}
      </div>
    </div>
  )
}

function CultureStatusIconPicker({
  value,
  onChange,
}: {
  value: CustomCultureStatusIcon
  onChange: (newIcon: CustomCultureStatusIcon) => void
}) {
  return (
    <div className={cs.iconPicker}>
      {CULTURE_STATUS_ICONS.map(icon => (
        <button
          key={icon}
          type='button'
          onClick={() => {
            onChange(icon)
          }}
        >
          <CultureStatusIcon
            kind={icon}
            className={cx(cs.icon, { [cs.active]: icon === value })}
          />
        </button>
      ))}
    </div>
  )
}

function CreateNewStatusForm({
  category,
  onCancel,
}: {
  category: CustomCultureStatusCategory
  onCancel: () => void
}) {
  const client = useBackendClient()

  const [newStatusName, setNewStatusName] = useState<string>('')
  const [newStatusIcon, setNewStatusIcon] = useState<CustomCultureStatusIcon>(
    category === 'terminated' ? 'TERMINATED' : 'ACTIVE',
  )

  const { mutate: submitNewStatus } = useMutation({
    mutationFn: async () =>
      await client.customCultureStatuses.create({
        name: newStatusName,
        icon: newStatusIcon,
        category,
      }),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEYS.CUSTOM_CULTURE_STATUS_LIST],
        }),
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEYS.WELL_PAGE],
        }),
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEYS.PLATE_PAGE],
        }),
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEYS.CULTURE_PAGE_FILTER_DEFINITIONS],
        }),
      ])
      onCancel()
    },
  })

  return (
    <div className={cs.createForm}>
      <p>Create new status</p>

      <CultureStatusIconPicker
        value={newStatusIcon}
        onChange={newIcon => {
          setNewStatusIcon(newIcon)
        }}
      />

      <input
        type='text'
        placeholder='Status name...'
        value={newStatusName}
        onChange={e => {
          if (e.target.value.trim().length <= 20) {
            setNewStatusName(e.target.value)
          }
        }}
        onKeyDown={e => {
          if (e.key === 'Enter') {
            submitNewStatus()
          }
        }}
      />

      {newStatusName.length >= 18 && (
        <div className={cs.warning}>A status can only be 20 characters long</div>
      )}

      <div className={cs.buttons}>
        <Button
          label='Cancel'
          type='danger'
          variant='minimal'
          onClick={() => {
            onCancel()
          }}
          className={cs.button}
        />
        <Button
          label='Save'
          type='primary'
          variant='minimal'
          disabled={newStatusName.trim() === ''}
          onClick={() => submitNewStatus()}
          className={cs.button}
        />
      </div>
    </div>
  )
}
